import { useEffect } from 'react';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useI18nText } from '@/lib/utils/i18nText';
import { isNonProductionRuntime } from '@/lib/config/runtimeEnv';

type DebugWindow = Window & {
  __ggcDebug?: {
    triggerUpdateBanner?: () => void;
  };
};

/** Local-time `YYYY-MM-DD HH:mm` for the deployed build timestamp. */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function UpdateBanner() {
  const { updateAvailable, deployedBuildTime, triggerForTest } = useVersionCheck();
  const { t } = useI18nText();

  useEffect(() => {
    if (!isNonProductionRuntime()) return;
    const w = window as DebugWindow;
    if (!w.__ggcDebug) w.__ggcDebug = {};
    w.__ggcDebug.triggerUpdateBanner = triggerForTest;
    return () => {
      delete w.__ggcDebug?.triggerUpdateBanner;
    };
  }, [triggerForTest]);

  if (!updateAvailable) return null;

  return (
    <div className="update-banner" role="alert" aria-live="assertive">
      <span className="update-banner__icon">
        <i className="fa fa-refresh" aria-hidden="true" />
      </span>
      <span className="update-banner__text">
        {t(
          'A new version has been deployed. Refresh to get the latest version.',
          '새 버전이 배포되었습니다. 새로고침하면 최신 버전을 사용할 수 있습니다.',
        )}
      </span>
      {deployedBuildTime && (
        <span className="update-banner__buildtime">
          <i className="fa fa-clock-o" aria-hidden="true" />
          {t('Built', '빌드')} {formatBuildTime(deployedBuildTime)}
        </span>
      )}
      <button
        type="button"
        className="update-banner__refresh-btn"
        onClick={() => window.location.reload()}
      >
        {t('Refresh now', '지금 새로고침')}
      </button>
    </div>
  );
}
